<template>
  <MaterialModal
    v-if="configModal.showMaterials"
    :materials="currentMaterials"
    :learningObjective="currentObjective"
    :config="configModal"
    :isAdaptativeLearning="false"
    @onCloseMaterialsModal="configModal.showMaterials = false"
    @onGoToObjective="goToMaterials(currentObjective.id)"
    @onGoToEditObject="goToEditObject"
    @onDeleteObject="deleteObject"
    @onChooseMaterial="getObjectsByLearningObjective"
  />
  <main>
    <div
      class="border-2 border-h-primary-200 p-4 py-4 rounded-md bg-neutral-50 flex justify-between"
    >
      <div>
        <h6 class="text-neutral-800 capitalize">
          {{ objective.name }}
        </h6>
        <p class="text-neutral-800 mt-1 text-[12px]">
          {{ objective.materials?.length }}
          {{
            $t("labels.generic", {
              pt: "Objetos adicionados",
              en: "Added objects",
            })
          }}
        </p>
      </div>
      <div class="flex items-center">
        <Button
          :id="`objective-add-material-btn`"
          class="btn-outline-bg-white flex items-center gap-2"
          v-if="user.role === 'TEACHER'"
          @click="goToMaterials(objective.id)"
        >
          <v-icon name="md-add" />
          <p class="hidden sm:block">
            {{ $t("labels.add", { pt: "objeto", en: "object" }) }}
          </p>
        </Button>
        <Button
          :id="`objective-view-material-btn`"
          class="btn-simple w-10"
          @click="getObjectsByLearningObjective(objective)"
        >
          <v-icon name="hi-solid-eye" />
        </Button>
        <Button
          :id="`objective-edit-btn`"
          class="btn-simple w-10"
          v-if="user.role === 'TEACHER'"
          @click="goToEditObjective(objective)"
        >
          <v-icon name="hi-solid-pencil" />
        </Button>

        <Button
          v-if="hasOrder"
          :id="`objective-list-btn`"
          class="btn-simple w-10"
          @click="$emit('onOrder', objective)"
        >
          <v-icon name="md-list" />
        </Button>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
  ConfigModal,
  Material,
  TypeModalHeader,
} from "@/models/material.model";
import { LearningObjective } from "@/models/learning-objective.model";
import { GoToRoute } from "@/utils/go-to.util";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { setItemLocalStorage } from "@/utils/save-storage.util";
import { ChaptersService } from "@/views/Chapters/chapters.service";
import { QueryParams } from "@/models/query-params.model";
import { take, last } from "rxjs";
import { User } from "@/models/user.model";
import { getUserFromStorage } from "@/utils/user-store.util";

export default defineComponent({
  name: "traditionalChapters",
  emits: ["onOpenModal", "onEditObjective", "onOrder"],
  props: {
    objective: { type: Object as PropType<any>, default: {} },
    chapter: { type: Object as PropType<any>, default: {} },
    courseId: [String],
    role: [String],
    hasOrder: Boolean,
  },
  computed: {
    go(): GoToRoute {
      return new GoToRoute();
    },
    rest(): MaterialsRest {
      return new MaterialsRest();
    },
    service(): ChaptersService {
      return new ChaptersService();
    },
    user(): User {
      return getUserFromStorage();
    },
  },
  data() {
    return {
      configModal: new ConfigModal(),
      currentObjective: {} as LearningObjective,
      currentMaterials: [] as Material[],
    };
  },
  methods: {
    goToEditObjective(objective: any): void {
      setItemLocalStorage("objective", JSON.stringify(this.chapter));
      let params = {
        courseId: this.courseId,
        chapterId: this.chapter.id,
        objectiveId: objective.id,
      };
      this.go.redirectToPage("formObjectiveTraditional", params);
    },
    getObjectsByLearningObjective(
      learningObjective: LearningObjective,
      page = 0 as number
    ): void {
      const params = new QueryParams(page, 1);
      this.service.material.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.currentMaterials = response?.content;
          if (this.currentMaterials) {
            this.configModal.showMaterials = true;
            this.currentObjective = learningObjective;
            this.configModal.maxIndexMaterial = response.total - 1;
            this.configModal.currentIndex = page;
            this.configModal.typeModalHeader =
              this.$route.name === "chapters"
                ? TypeModalHeader.TEACHER_TRADITIONAL_COURSE
                : TypeModalHeader.TEACHER_LINEAR;
          }
        },
      });
      this.service.getObjectsByLearningObjective(learningObjective.id, params);
    },
    openModal(objective: LearningObjective): void {
      this.currentObjective = objective;
      this.currentMaterials = objective.materials ? objective.materials : [];
      this.configModal.currentIndex = 0;
      this.configModal.maxIndexMaterial = objective.materials
        ? objective.materials.length
        : 0;
      this.configModal.showMaterials = true;
      this.configModal.typeModalHeader =
        this.$route.name === "chapters"
          ? TypeModalHeader.TEACHER_TRADITIONAL_COURSE
          : TypeModalHeader.TEACHER_LINEAR;
    },
    goToEditObject(material: any): void {
      const params = {
        objectiveId: this.currentObjective.id,
        id: material.id,
        type: "edit",
      };
      this.go.redirectToPage("formObjectTraditional", params);
    },
    goToMaterials(objectiveId: string): void {
      let params = {
        objectiveId: objectiveId,
        type: "add",
        courseId: this.courseId,
      };
      this.go.redirectToPage("formObjectTraditional", params);
    },
    deleteObject(material: any) {
      this.rest
        .deleteMaterial(this.currentObjective.id, material.id)
        .subscribe({
          next: () => {
            const materials = this.currentMaterials;
            const filtered = materials.filter((e: any) => e.id !== material.id);
            this.currentObjective.materials = filtered;
            setTimeout(() => {
              this.getObjectsByLearningObjective(this.currentObjective);
            }, 300);
          },
        });
    },
  },
});
</script>
